import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react"
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import {
  emptyFlowHistory,
  pushFlowHistory,
  redoFlowHistory,
  undoFlowHistory,
  type FlowHistoryCommand,
  type FlowHistoryEntry,
  type FlowHistoryPush,
  type FlowHistoryState,
  type FlowPoint,
} from "@/lib/flow-board-history"
import {
  createFlowEdge,
  deleteFlowEdge,
  deleteFlowNode,
  purgeDiscardedFlowNodes,
  purgeFlowEdge,
  purgeFlowNode,
  restoreFlowEdge,
  restoreFlowNode,
  updateFlowNode,
} from "@/lib/flow-api"
import { mergeFlowNodes, type FlowNodeInput, type FlowNodeRow } from "@/types/flow"

let optimisticInputSeq = 0

function nextOptimisticInputId(): number {
  optimisticInputSeq -= 1
  return optimisticInputSeq
}

type UseFlowBoardHistoryArgs = {
  projectId: number
  flowId: number | null
  canEdit: boolean
  nodeRows: FlowNodeRow[]
  setNodeRows: Dispatch<SetStateAction<FlowNodeRow[]>>
  setSelectedIds: Dispatch<SetStateAction<number[]>>
  onGraphChange?: () => void
}

export function useFlowBoardHistory({
  projectId,
  flowId,
  canEdit,
  nodeRows,
  setNodeRows,
  setSelectedIds,
  onGraphChange,
}: UseFlowBoardHistoryArgs) {
  const { t } = useTranslation("flow")
  const [history, setHistoryState] = useState<FlowHistoryState>(emptyFlowHistory)
  const historyRef = useRef(history)
  const rowsRef = useRef(nodeRows)
  const flowIdRef = useRef(flowId)
  const projectIdRef = useRef(projectId)
  const onGraphChangeRef = useRef(onGraphChange)
  onGraphChangeRef.current = onGraphChange
  const notifyGraph = useCallback(() => {
    onGraphChangeRef.current?.()
  }, [])
  const canEditRef = useRef(canEdit)
  const tailRef = useRef(Promise.resolve())
  const historyBusyRef = useRef(false)
  const pendingDeletedRef = useRef(new Set<number>())
  const pendingDeletedEdgesRef = useRef(new Set<number>())
  const deletedIdsRef = useRef<number[]>([])
  const deletedEdgeIdsRef = useRef<number[]>([])
  const tRef = useRef(t)

  rowsRef.current = nodeRows
  projectIdRef.current = projectId
  canEditRef.current = canEdit
  tRef.current = t

  const syncDeleted = useCallback(() => {
    const ids = new Set(historyRef.current.deletedIds)
    for (const id of pendingDeletedRef.current) ids.add(id)
    deletedIdsRef.current = [...ids]
    const edgeIds = new Set(historyRef.current.deletedEdgeIds)
    for (const id of pendingDeletedEdgesRef.current) edgeIds.add(id)
    deletedEdgeIdsRef.current = [...edgeIds]
  }, [])

  const setHistory = useCallback(
    (next: FlowHistoryState) => {
      historyRef.current = next
      setHistoryState(next)
      syncDeleted()
    },
    [syncDeleted],
  )

  const fail = useCallback((error: unknown) => {
    toast.error(error instanceof Error ? error.message : tRef.current("errors.nodeFailed"))
  }, [])

  const enqueue = useCallback((task: () => Promise<void>) => {
    const run = tailRef.current.then(task, task)
    tailRef.current = run.then(
      () => undefined,
      () => undefined,
    )
    return run
  }, [])

  const commit = useCallback(
    (result: FlowHistoryPush) => {
      setHistory(result.state)
      const project = projectIdRef.current
      for (const id of result.purgeIds) {
        void purgeFlowNode(project, id).catch(fail)
      }
      for (const id of result.purgeEdgeIds) {
        void purgeFlowEdge(project, id).catch(fail)
      }
    },
    [fail, setHistory],
  )

  /** setNodeRows, который сразу кладёт результат в rowsRef. */
  const updateRows = useCallback(
    (update: (prev: FlowNodeRow[]) => FlowNodeRow[]) => {
      setNodeRows((prev) => {
        const next = update(prev)
        rowsRef.current = next
        return next
      })
    },
    [setNodeRows],
  )

  const replaceLocal = useCallback(
    (node: FlowNodeRow) => {
      updateRows((prev) => prev.map((row) => (row.id === node.id ? node : row)))
    },
    [updateRows],
  )

  const replaceNodes = useCallback(
    (nodes: FlowNodeRow[]) => {
      updateRows((prev) => mergeFlowNodes(prev, nodes))
    },
    [updateRows],
  )

  const removeLocal = useCallback(
    (nodeId: number) => {
      updateRows((prev) => prev.filter((row) => row.id !== nodeId))
      setSelectedIds((current) => (current.includes(nodeId) ? current.filter((id) => id !== nodeId) : current))
    },
    [setSelectedIds, updateRows],
  )

  const removeLocalMany = useCallback(
    (nodeIds: number[]) => {
      const drop = new Set(nodeIds)
      updateRows((prev) => prev.filter((row) => !drop.has(row.id)))
      setSelectedIds((current) => {
        const next = current.filter((id) => !drop.has(id))
        return next.length === current.length ? current : next
      })
    },
    [setSelectedIds, updateRows],
  )

  const upsertLocal = useCallback(
    (node: FlowNodeRow) => {
      updateRows((prev) =>
        prev
          .filter((row) => row.id !== node.id)
          .concat(node)
          .sort((a, b) => a.id - b.id),
      )
    },
    [updateRows],
  )

  // setNodeRows доезжает до rowsRef только на рендере. Связь с выхода целится в только
  // что созданную ноду в том же продолжении, и без этого connectNodes её не находит.
  const stageNodes = useCallback((incoming: readonly FlowNodeRow[]) => {
    if (incoming.length === 0) return
    rowsRef.current = mergeFlowNodes(rowsRef.current, incoming)
  }, [])

  const applyEntry = useCallback(
    async (entry: FlowHistoryEntry, direction: "undo" | "redo", boardId: number): Promise<number | null> => {
      const project = projectIdRef.current
      const forward = direction === "redo"
      if (entry.type === "move") {
        const point = forward ? entry.to : entry.from
        const node = await updateFlowNode(project, entry.nodeId, { position_x: point.x, position_y: point.y })
        if (flowIdRef.current !== boardId) return null
        replaceLocal(node)
        return null
      }
      if (entry.type === "resize") {
        const width = forward ? entry.to : entry.from
        const point = entry.position ? (forward ? entry.position.to : entry.position.from) : null
        const node = await updateFlowNode(project, entry.nodeId, {
          display_width: width,
          ...(point ? { position_x: point.x, position_y: point.y } : {}),
        })
        if (flowIdRef.current !== boardId) return null
        replaceLocal(node)
        return null
      }
      if (entry.type === "connect" || entry.type === "disconnect") {
        const removing = (entry.type === "connect" && !forward) || (entry.type === "disconnect" && forward)
        const result = removing
          ? await deleteFlowEdge(project, entry.edgeId)
          : await restoreFlowEdge(project, entry.edgeId)
        if (flowIdRef.current !== boardId) return null
        replaceNodes(result.nodes)
        return null
      }
      const removing = (entry.type === "create" && !forward) || (entry.type === "delete" && forward)
      if (removing) {
        const nodes = await deleteFlowNode(project, entry.nodeId)
        if (flowIdRef.current !== boardId) return null
        removeLocal(entry.nodeId)
        replaceNodes(nodes)
        return null
      }
      const restored = await restoreFlowNode(project, entry.nodeId)
      if (flowIdRef.current !== boardId) return null
      replaceNodes(restored.nodes)
      return restored.node.id
    },
    [removeLocal, replaceLocal, replaceNodes],
  )

  const applyCommand = useCallback(
    async (command: FlowHistoryCommand, direction: "undo" | "redo", boardId: number) => {
      const entries = command.type === "batch" ? command.commands : [command]
      const ordered = direction === "undo" ? [...entries].reverse() : entries
      const restored: number[] = []
      for (const entry of ordered) {
        if (flowIdRef.current !== boardId) return
        const restoredId = await applyEntry(entry, direction, boardId)
        if (restoredId != null) restored.push(restoredId)
      }
      if (flowIdRef.current === boardId && restored.length > 0) setSelectedIds(restored)
      const structural = ordered.some((entry) => entry.type !== "move")
      if (flowIdRef.current === boardId && structural) notifyGraph()
    },
    [applyEntry, notifyGraph, setSelectedIds],
  )

  const runHistory = useCallback(
    (task: (boardId: number) => Promise<void>) => {
      if (!canEditRef.current || historyBusyRef.current || flowIdRef.current == null) return
      historyBusyRef.current = true
      const boardId = flowIdRef.current
      void enqueue(async () => {
        try {
          if (flowIdRef.current !== boardId) return
          await task(boardId)
        } catch (error: unknown) {
          if (flowIdRef.current === boardId) fail(error)
        } finally {
          historyBusyRef.current = false
        }
      })
    },
    [enqueue, fail],
  )

  const recordCreate = useCallback(
    (node: FlowNodeRow) => {
      if (!canEditRef.current || flowIdRef.current == null) return
      const boardId = flowIdRef.current
      void enqueue(async () => {
        if (flowIdRef.current !== boardId) return
        commit(pushFlowHistory(historyRef.current, { type: "create", nodeId: node.id }))
      })
    },
    [commit, enqueue],
  )

  const recordCreatedGraph = useCallback(
    (nodes: FlowNodeRow[], options?: { includeEdges?: boolean; edgeIds?: number[] }) => {
      if (!canEditRef.current || flowIdRef.current == null || nodes.length === 0) return
      const boardId = flowIdRef.current
      const commands: FlowHistoryEntry[] = nodes.map((node) => ({ type: "create", nodeId: node.id }))
      // Явный список — только новые рёбра. Обход inputs цели записал бы и старые входы.
      if (options?.edgeIds) {
        for (const edgeId of options.edgeIds) commands.push({ type: "connect", edgeId })
      } else if (options?.includeEdges !== false) {
        const seen = new Set<number>()
        for (const node of nodes) {
          for (const input of node.inputs ?? []) {
            if (seen.has(input.id)) continue
            seen.add(input.id)
            commands.push({ type: "connect", edgeId: input.id })
          }
        }
      }
      void enqueue(async () => {
        if (flowIdRef.current !== boardId) return
        const only = commands[0]
        const command: FlowHistoryCommand = commands.length === 1 && only ? only : { type: "batch", commands }
        commit(pushFlowHistory(historyRef.current, command))
      })
    },
    [commit, enqueue],
  )

  const deleteNode = useCallback(
    (nodeId: number) => {
      if (!canEditRef.current || historyBusyRef.current || flowIdRef.current == null) return
      const snapshot = rowsRef.current.find((node) => node.id === nodeId)
      if (!snapshot) return
      historyBusyRef.current = true
      const boardId = flowIdRef.current
      pendingDeletedRef.current.add(nodeId)
      syncDeleted()
      removeLocal(nodeId)
      void enqueue(async () => {
        try {
          const nodes = await deleteFlowNode(projectIdRef.current, nodeId)
          pendingDeletedRef.current.delete(nodeId)
          if (flowIdRef.current !== boardId) return
          replaceNodes(nodes)
          commit(pushFlowHistory(historyRef.current, { type: "delete", nodeId }))
          notifyGraph()
        } catch (error: unknown) {
          pendingDeletedRef.current.delete(nodeId)
          if (flowIdRef.current === boardId) {
            upsertLocal(snapshot)
            syncDeleted()
            fail(error)
          }
        } finally {
          historyBusyRef.current = false
        }
      })
    },
    [commit, enqueue, fail, notifyGraph, removeLocal, replaceNodes, syncDeleted, upsertLocal],
  )

  const deleteNodes = useCallback(
    (ids: number[]) => {
      const unique = [...new Set(ids)]
      if (unique.length === 0) return
      if (unique.length === 1) {
        const only = unique[0]
        if (only != null) deleteNode(only)
        return
      }
      if (!canEditRef.current || historyBusyRef.current || flowIdRef.current == null) return
      const snapshots = unique.flatMap((id) => {
        const snapshot = rowsRef.current.find((node) => node.id === id)
        return snapshot ? [snapshot] : []
      })
      if (snapshots.length === 0) return
      historyBusyRef.current = true
      const boardId = flowIdRef.current
      for (const snapshot of snapshots) pendingDeletedRef.current.add(snapshot.id)
      syncDeleted()
      removeLocalMany(snapshots.map((node) => node.id))
      const batchIds = new Set(snapshots.map((node) => node.id))
      void enqueue(async () => {
        const deleted: FlowHistoryEntry[] = []
        const updated: FlowNodeRow[] = []
        try {
          for (const snapshot of snapshots) {
            const nodes = await deleteFlowNode(projectIdRef.current, snapshot.id)
            pendingDeletedRef.current.delete(snapshot.id)
            updated.push(...nodes)
            deleted.push({ type: "delete", nodeId: snapshot.id })
          }
          if (flowIdRef.current !== boardId || deleted.length === 0) return
          // Ответ удаления — соседи по рёбрам. Сосед из этой же пачки ещё жив в раннем
          // ответе и без фильтра mergeFlowNodes возвращает его на доску.
          const survivors = updated.filter((node) => !batchIds.has(node.id))
          if (survivors.length > 0) replaceNodes(survivors)
          commit(pushFlowHistory(historyRef.current, { type: "batch", commands: deleted }))
          notifyGraph()
        } catch (error: unknown) {
          for (const snapshot of snapshots) {
            if (deleted.some((entry) => entry.type === "delete" && entry.nodeId === snapshot.id)) continue
            pendingDeletedRef.current.delete(snapshot.id)
            if (flowIdRef.current === boardId) upsertLocal(snapshot)
          }
          syncDeleted()
          const deletedIds = new Set(deleted.flatMap((entry) => (entry.type === "delete" ? [entry.nodeId] : [])))
          const survivors = updated.filter((node) => !deletedIds.has(node.id))
          if (flowIdRef.current === boardId && survivors.length > 0) replaceNodes(survivors)
          if (flowIdRef.current === boardId && deleted.length > 0) {
            const command: FlowHistoryCommand = deleted.length === 1 ? deleted[0] : { type: "batch", commands: deleted }
            commit(pushFlowHistory(historyRef.current, command))
          }
          if (flowIdRef.current === boardId) fail(error)
        } finally {
          historyBusyRef.current = false
        }
      })
    },
    [commit, deleteNode, enqueue, fail, notifyGraph, removeLocalMany, replaceNodes, syncDeleted, upsertLocal],
  )

  const connectNodes = useCallback(
    (sourceId: number, targetId: number, ports: { sourcePort: string; targetPort: string }) => {
      if (!canEditRef.current || historyBusyRef.current || flowIdRef.current == null) return
      const target = rowsRef.current.find((node) => node.id === targetId)
      if (!target) return
      historyBusyRef.current = true
      const boardId = flowIdRef.current
      const snapshot = target
      const optimistic: FlowNodeInput = {
        id: nextOptimisticInputId(),
        source_node_id: sourceId,
        source_port: ports.sourcePort,
        target_port: ports.targetPort,
        position: target.inputs?.length ?? 0,
      }
      replaceLocal({ ...target, inputs: [...(target.inputs ?? []), optimistic] })
      void enqueue(async () => {
        try {
          const result = await createFlowEdge(projectIdRef.current, boardId, sourceId, targetId, ports)
          if (flowIdRef.current !== boardId) return
          replaceNodes(result.nodes)
          const edge = result.node.inputs?.find(
            (input) =>
              input.source_node_id === sourceId &&
              (input.source_port ?? "image") === ports.sourcePort &&
              (input.target_port ?? "references") === ports.targetPort,
          )
          if (!edge) return
          commit(pushFlowHistory(historyRef.current, { type: "connect", edgeId: edge.id }))
          notifyGraph()
        } catch (error: unknown) {
          if (flowIdRef.current === boardId) {
            replaceLocal(snapshot)
            fail(error)
          }
        } finally {
          historyBusyRef.current = false
        }
      })
    },
    [commit, enqueue, fail, notifyGraph, replaceLocal, replaceNodes],
  )

  const disconnectEdge = useCallback(
    (edgeId: number) => {
      if (!canEditRef.current || historyBusyRef.current || flowIdRef.current == null) return
      const snapshot = rowsRef.current.find((node) => node.inputs?.some((input) => input.id === edgeId))
      if (!snapshot) return
      historyBusyRef.current = true
      const boardId = flowIdRef.current
      pendingDeletedEdgesRef.current.add(edgeId)
      syncDeleted()
      replaceLocal({
        ...snapshot,
        inputs: (snapshot.inputs ?? []).filter((input) => input.id !== edgeId),
      })
      void enqueue(async () => {
        try {
          const result = await deleteFlowEdge(projectIdRef.current, edgeId)
          pendingDeletedEdgesRef.current.delete(edgeId)
          if (flowIdRef.current !== boardId) return
          replaceNodes(result.nodes)
          commit(pushFlowHistory(historyRef.current, { type: "disconnect", edgeId }))
          notifyGraph()
        } catch (error: unknown) {
          pendingDeletedEdgesRef.current.delete(edgeId)
          if (flowIdRef.current === boardId) {
            replaceLocal(snapshot)
            syncDeleted()
            fail(error)
          }
        } finally {
          historyBusyRef.current = false
        }
      })
    },
    [commit, enqueue, fail, notifyGraph, replaceLocal, replaceNodes, syncDeleted],
  )

  const moveNode = useCallback(
    (id: number, x: number, y: number) => {
      if (!canEditRef.current || flowIdRef.current == null) return
      const current = rowsRef.current.find((node) => node.id === id)
      if (!current || (current.position_x === x && current.position_y === y)) return
      const from = { x: current.position_x, y: current.position_y }
      const boardId = flowIdRef.current
      updateRows((prev) => prev.map((node) => (node.id === id ? { ...node, position_x: x, position_y: y } : node)))
      void enqueue(async () => {
        try {
          await updateFlowNode(projectIdRef.current, id, { position_x: x, position_y: y })
          if (flowIdRef.current !== boardId) return
          commit(pushFlowHistory(historyRef.current, { type: "move", nodeId: id, from, to: { x, y } }))
        } catch (error: unknown) {
          if (flowIdRef.current !== boardId) return
          updateRows((prev) =>
            prev.map((node) => (node.id === id ? { ...node, position_x: from.x, position_y: from.y } : node)),
          )
          fail(error)
        }
      })
    },
    [commit, enqueue, fail, updateRows],
  )

  const moveNodes = useCallback(
    (updates: { id: number; x: number; y: number; displayWidth?: number | null }[]) => {
      if (!canEditRef.current || flowIdRef.current == null) return
      const changes: {
        id: number
        from: FlowPoint
        to: FlowPoint
        fromWidth: number | null
        toWidth: number | null
        widthChanged: boolean
      }[] = []
      for (const update of updates) {
        const current = rowsRef.current.find((node) => node.id === update.id)
        if (!current) continue
        const moved = current.position_x !== update.x || current.position_y !== update.y
        const widthChanged = update.displayWidth !== undefined && update.displayWidth !== current.display_width
        if (!moved && !widthChanged) continue
        changes.push({
          id: update.id,
          from: { x: current.position_x, y: current.position_y },
          to: { x: update.x, y: update.y },
          fromWidth: current.display_width,
          toWidth: widthChanged ? (update.displayWidth ?? null) : current.display_width,
          widthChanged,
        })
      }
      if (changes.length === 0) return
      const only = changes[0]
      if (changes.length === 1 && only && !only.widthChanged) {
        moveNode(only.id, only.to.x, only.to.y)
        return
      }
      const boardId = flowIdRef.current
      const apply = (nextOf: "to" | "from") => {
        updateRows((prev) => {
          const byId = new Map(changes.map((change) => [change.id, change]))
          return prev.map((node) => {
            const change = byId.get(node.id)
            if (!change) return node
            const point = nextOf === "to" ? change.to : change.from
            const width = nextOf === "to" ? change.toWidth : change.fromWidth
            return {
              ...node,
              position_x: point.x,
              position_y: point.y,
              ...(change.widthChanged ? { display_width: width } : {}),
            }
          })
        })
      }
      apply("to")
      void enqueue(async () => {
        try {
          await Promise.all(
            changes.map((change) =>
              updateFlowNode(projectIdRef.current, change.id, {
                position_x: change.to.x,
                position_y: change.to.y,
                ...(change.widthChanged ? { display_width: change.toWidth } : {}),
              }),
            ),
          )
          if (flowIdRef.current !== boardId) return
          const commands: FlowHistoryEntry[] = changes.map((change) => {
            if (!change.widthChanged) return { type: "move", nodeId: change.id, from: change.from, to: change.to }
            const position =
              change.from.x !== change.to.x || change.from.y !== change.to.y
                ? { from: change.from, to: change.to }
                : undefined
            return {
              type: "resize",
              nodeId: change.id,
              from: change.fromWidth,
              to: change.toWidth,
              position,
            }
          })
          const command: FlowHistoryCommand =
            commands.length === 1 && commands[0] ? commands[0] : { type: "batch", commands }
          commit(pushFlowHistory(historyRef.current, command))
        } catch (error: unknown) {
          if (flowIdRef.current !== boardId) return
          apply("from")
          fail(error)
        }
      })
    },
    [commit, enqueue, fail, moveNode, updateRows],
  )

  const resizeNode = useCallback(
    (id: number, width: number, point: FlowPoint) => {
      if (!canEditRef.current || flowIdRef.current == null) return
      const current = rowsRef.current.find((node) => node.id === id)
      if (!current) return
      const fromPoint = { x: current.position_x, y: current.position_y }
      const moved = fromPoint.x !== point.x || fromPoint.y !== point.y
      if (current.display_width === width && !moved) return
      const from = current.display_width
      const position = moved ? { from: fromPoint, to: point } : undefined
      const boardId = flowIdRef.current
      const place = (displayWidth: number | null, at: FlowPoint) =>
        updateRows((prev) =>
          prev.map((node) =>
            node.id === id ? { ...node, display_width: displayWidth, position_x: at.x, position_y: at.y } : node,
          ),
        )
      place(width, point)
      void enqueue(async () => {
        try {
          await updateFlowNode(projectIdRef.current, id, {
            display_width: width,
            ...(moved ? { position_x: point.x, position_y: point.y } : {}),
          })
          if (flowIdRef.current !== boardId) return
          commit(pushFlowHistory(historyRef.current, { type: "resize", nodeId: id, from, to: width, position }))
        } catch (error: unknown) {
          if (flowIdRef.current !== boardId) return
          place(from, fromPoint)
          fail(error)
        }
      })
    },
    [commit, enqueue, fail, updateRows],
  )

  const undo = useCallback(() => {
    runHistory(async (boardId) => {
      const step = undoFlowHistory(historyRef.current)
      if (!step) return
      await applyCommand(step.command, "undo", boardId)
      if (flowIdRef.current !== boardId) return
      setHistory(step.state)
    })
  }, [applyCommand, runHistory, setHistory])

  const redo = useCallback(() => {
    runHistory(async (boardId) => {
      const step = redoFlowHistory(historyRef.current)
      if (!step) return
      await applyCommand(step.command, "redo", boardId)
      if (flowIdRef.current !== boardId) return
      setHistory(step.state)
    })
  }, [applyCommand, runHistory, setHistory])

  useEffect(() => {
    const previous = flowIdRef.current
    if (previous === flowId) return
    flowIdRef.current = flowId
    if (previous == null) return
    const leaving = previous
    void enqueue(async () => {
      setHistory(emptyFlowHistory())
      pendingDeletedRef.current.clear()
      pendingDeletedEdgesRef.current.clear()
      syncDeleted()
      try {
        await purgeDiscardedFlowNodes(projectIdRef.current, leaving)
      } catch (error: unknown) {
        fail(error)
      }
    })
  }, [enqueue, fail, flowId, setHistory, syncDeleted])

  return {
    deletedIdsRef,
    deletedEdgeIdsRef,
    recordCreate,
    recordCreatedGraph,
    stageNodes,
    deleteNode,
    deleteNodes,
    connectNodes,
    disconnectEdge,
    moveNode,
    moveNodes,
    resizeNode,
    undo,
    redo,
  }
}

export function isFlowBoardTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return target.closest("input, textarea, select, [contenteditable='true']") != null
}
